import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { Comic } from '../../types';
import { getImageUrl } from '../../api';
import { LazyImage } from '../ui/LazyImage';
import { useFieldVisibility } from '../../hooks/useFieldVisibility';
import { 
  Bars3Icon
} from '@heroicons/react/24/outline';

interface ListItemCardProps {
  comic: Comic;
  onComicClick: (comic: Comic) => void;
  isDragDisabled?: boolean;
}

const ListItemCard: React.FC<ListItemCardProps> = ({
  comic,
  onComicClick,
  isDragDisabled = false
}) => {
  const { t } = useTranslation();
  const { isFieldVisible } = useFieldVisibility();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: comic.id.toString(),
    disabled: isDragDisabled
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'owned':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'wishlist':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ordered':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'loaned':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 
        hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200
        ${isDragging ? 'shadow-lg opacity-50' : 'shadow-sm hover:shadow-md'}
      `}
    >
      <div className="flex items-center p-4 space-x-4">
        {/* Drag Handle */}
        {!isDragDisabled && (
          <div
            {...attributes}
            {...listeners}
            className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            style={{ touchAction: 'none' }}
          >
            <Bars3Icon className="h-5 w-5" />
          </div>
        )}

        {/* Cover Image */}
        {isFieldVisible('cover_image') && (
          <div className="flex-shrink-0 w-16 h-20 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
            {comic.cover_image ? (
              <LazyImage 
                src={getImageUrl(comic.cover_image)} 
                alt={comic.title}
                className="w-full h-full object-cover"
                skeletonClassName="w-full h-full"
                fallbackSrc=""
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
        )}

        {/* Comic Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              {comic.series && isFieldVisible('series') && (
                <p className="text-sm text-gray-600 dark:text-gray-300 truncate font-medium">
                  {comic.series} {comic.issue_number && isFieldVisible('issue_number') && comic.issue_number}
                </p>
              )}
              
              <h3 
                className="text-lg font-medium text-gray-900 dark:text-gray-100 truncate cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                onClick={() => onComicClick(comic)}
              >
                {comic.title}
              </h3>

              <div className="flex flex-wrap gap-2 mt-2">
                {comic.people.length > 0 && isFieldVisible('people') && (
                  <div className="flex flex-wrap gap-1">
                    {comic.people.slice(0, 3).map((person) => (
                      <span
                        key={person.id}
                        className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs rounded"
                      >
                        {person.name}
                      </span>
                    ))}
                    {comic.people.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs rounded">
                        {t('common.moreItems', { count: comic.people.length - 3 })}
                      </span>
                    )}
                  </div>
                )}

                {comic.tags && comic.tags.length > 0 && isFieldVisible('tags') && (
                  <div className="flex flex-wrap gap-1">
                    {comic.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag.id}
                        className="px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-900 dark:text-blue-100 text-xs rounded"
                      >
                        {tag.name}
                      </span>
                    ))}
                    {comic.tags.length > 3 && (
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-900 dark:text-blue-100 text-xs rounded">
                        {t('common.moreItems', { count: comic.tags.length - 3 })}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Status Badge */}
            {comic.status && isFieldVisible('status') && (
              <div className={`flex-shrink-0 ml-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(comic.status)}`}>
                {t(`status.${comic.status}`)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListItemCard;